import type { ComponentChildren } from 'preact';

import { Card } from '@amumin/ui';
import type { IllustrationId } from '@amumin/ui-core';

import { isLocaleCode, LOCALE_INFO, LOCALES } from '@amumin/i18n';

import { appText, browserLocale, messages, setBrowserLocale } from '../state/session.js';
import { BrandLogo } from './BrandLogo.js';
import { CompanyDetails } from './CompanyDetails.js';

/**
 * Gecentreerde kaart (440 px) op het rustige canvas voor de schermen zonder sessie (DESIGN.md
 * §8.W12): logo-regel erboven. Fijnslijpen 7 okt 2026: geen illustratie meer (volwassen context,
 * besluit professioneel-bord); de prop blijft zodat de schermen niet hoeven te veranderen.
 */
export function AuthLayout({
  title,
  children,
}: {
  title: string;
  children: ComponentChildren;
  illustration?: IllustrationId;
}) {
  const m = messages.value;
  return (
    <main class="auth-page">
      <p class="auth-brand">
        <BrandLogo label={m.web.appName} />
      </p>
      <Card title={title}>{children}</Card>
      {/* Golf 3: taal vóór het inloggen (de namen in hun eigen schrift); blijft in deze browser. */}
      <label class="auth-language">
        <span class="fb-sr-only">{appText.value.languages.authLabel}</span>
        <select
          name="auth-language"
          data-name="auth-language"
          value={browserLocale.value}
          onChange={(e) => {
            const v = (e.currentTarget as HTMLSelectElement).value;
            if (isLocaleCode(v)) setBrowserLocale(v);
          }}
        >
          {LOCALES.map((code) => (
            <option key={code} value={code} lang={LOCALE_INFO[code].lang}>
              {LOCALE_INFO[code].nativeName}
            </option>
          ))}
        </select>
      </label>
      <CompanyDetails />
    </main>
  );
}

/** Token uit het URL-fragment (`#token=…`, ONTWERP.md §11.1.2) of, als terugval, uit de query. */
export function tokenFromUrl(query: Record<string, string>): string | null {
  try {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
    const fromHash = new URLSearchParams(hash).get('token');
    if (fromHash) return fromHash;
  } catch {
    // geen hash
  }
  return query.token ?? null;
}
